import React, { useCallback, useLayoutEffect, useRef } from 'react';
import {
  assignRoundRobinGridTracks,
  masonryColumnCount,
  masonryFooterRowStart,
  MASONRY_BREAKPOINT,
  MASONRY_GAP_PX,
  MASONRY_MIN_COL_WIDTH,
  MASONRY_ROW_UNIT,
} from './eventsMasonry';

const HEIGHT_TOLERANCE_PX = 1;

function isShowMoreCard(el) {
  return el.classList.contains('event-pub-card--show-more');
}

function heightPx(el) {
  return Math.round(el.getBoundingClientRect().height);
}

/**
 * Round-robin pack at every width. Rank reads left to right.
 * A short card gets the next card in that column directly under it.
 * SHOW MORE spans the full width below the tallest column.
 * `minColWidth` / `mobileColumns` / `gapPx` shrink a grid (Save the date) without changing day groups.
 * An explicit `gapPx` also sets the inline column gap so columns and rows match.
 */
const EventsMasonryGrid = ({
  children,
  className = '',
  minColWidth = MASONRY_MIN_COL_WIDTH,
  mobileColumns = 2,
  gapPx,
}) => {
  const gap = gapPx == null ? MASONRY_GAP_PX : Math.max(0, Number(gapPx) || 0);
  const ref = useRef(null);
  const lastWidthRef = useRef(-1);
  const forceLayoutRef = useRef(true);
  const recordedHeightsRef = useRef(new Map());
  const observedCardsRef = useRef(new Set());
  const resizeObserverRef = useRef(null);
  const layoutRef = useRef(() => {});

  const syncCardObservers = useCallback((cards) => {
    const ro = resizeObserverRef.current;
    if (!ro) return;
    const next = new Set(cards);
    observedCardsRef.current.forEach((el) => {
      if (next.has(el)) return;
      ro.unobserve(el);
      recordedHeightsRef.current.delete(el);
    });
    cards.forEach((el) => {
      if (!observedCardsRef.current.has(el)) ro.observe(el);
    });
    observedCardsRef.current = next;
  }, []);

  const layout = useCallback(() => {
    const grid = ref.current;
    if (!grid) return;

    const width = grid.clientWidth;
    const widthUnchanged = width === lastWidthRef.current;
    if (!forceLayoutRef.current && widthUnchanged) return;
    forceLayoutRef.current = false;
    lastWidthRef.current = width;

    const desktop =
      typeof window.matchMedia === 'function' &&
      window.matchMedia(`(min-width: ${MASONRY_BREAKPOINT}px)`).matches;
    const kids = Array.from(grid.children);
    const cards = kids.filter((el) => !isShowMoreCard(el));
    const footers = kids.filter(isShowMoreCard);
    const cols = desktop
      ? masonryColumnCount(width, minColWidth, gap)
      : Math.max(1, Math.floor(Number(mobileColumns) || 2));

    grid.classList.add('events-grid--masonry');
    grid.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
    if (gapPx != null) grid.style.columnGap = `${gap}px`;
    void grid.offsetHeight;

    const heights = cards.map((el) => el.getBoundingClientRect().height);
    const footerHeights = footers.map((el) => el.getBoundingClientRect().height);
    const tracks = assignRoundRobinGridTracks(
      heights,
      cols,
      MASONRY_ROW_UNIT,
      gap
    );

    tracks.forEach((track, i) => {
      const el = cards[i];
      if (!el) return;
      el.style.gridColumn = String(track.column);
      el.style.gridRow = `${track.rowStart} / span ${track.rowSpan}`;
    });

    if (footers.length > 0) {
      const footerStart = masonryFooterRowStart(tracks, MASONRY_ROW_UNIT, gap);
      footers.forEach((el, i) => {
        const span = Math.max(1, Math.ceil((footerHeights[i] || 0) / MASONRY_ROW_UNIT));
        el.style.gridColumn = '1 / -1';
        el.style.gridRow = `${footerStart} / span ${span}`;
      });
    }

    const recorded = recordedHeightsRef.current;
    const live = new Set(cards);
    recorded.forEach((_, el) => {
      if (!live.has(el)) recorded.delete(el);
    });
    cards.forEach((el, i) => {
      recorded.set(el, Math.round(heights[i]));
    });
    syncCardObservers(cards);
  }, [gap, gapPx, minColWidth, mobileColumns, syncCardObservers]);

  layoutRef.current = layout;

  useLayoutEffect(() => {
    forceLayoutRef.current = true;
    layout();
  }, [layout, children]);

  useLayoutEffect(() => {
    const grid = ref.current;
    if (!grid) return undefined;

    let raf = 0;
    let fontsCancelled = false;

    const schedule = (force = false) => {
      if (force) forceLayoutRef.current = true;
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        layoutRef.current();
      });
    };

    const onResize = () => {
      const node = ref.current;
      if (!node) return;
      const widthChanged = node.clientWidth !== lastWidthRef.current;
      const heightChanged = Array.from(node.children).some((el) => {
        if (isShowMoreCard(el)) return false;
        const recorded = recordedHeightsRef.current.get(el);
        if (recorded == null) return true;
        return Math.abs(heightPx(el) - recorded) > HEIGHT_TOLERANCE_PX;
      });
      if (widthChanged || heightChanged) schedule(true);
    };

    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;
    resizeObserverRef.current = ro;
    if (ro) {
      ro.observe(grid);
      syncCardObservers(Array.from(grid.children).filter((el) => !isShowMoreCard(el)));
    }

    const mo = new MutationObserver(() => schedule(true));
    mo.observe(grid, { childList: true });

    const onLoad = () => schedule(true);
    grid.addEventListener('load', onLoad, true);

    const mq =
      typeof window.matchMedia === 'function'
        ? window.matchMedia(`(min-width: ${MASONRY_BREAKPOINT}px)`)
        : null;
    const onMq = () => schedule(true);
    if (mq) {
      if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onMq);
      else mq.addListener(onMq);
    }

    const fontsReady = typeof document !== 'undefined' ? document.fonts?.ready : null;
    if (fontsReady && typeof fontsReady.then === 'function') {
      fontsReady.then(() => {
        if (!fontsCancelled) schedule(true);
      });
    }

    return () => {
      fontsCancelled = true;
      if (raf) window.cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      resizeObserverRef.current = null;
      observedCardsRef.current = new Set();
      mo.disconnect();
      grid.removeEventListener('load', onLoad, true);
      if (!mq) return;
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', onMq);
      else mq.removeListener(onMq);
    };
  }, [syncCardObservers]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

export default EventsMasonryGrid;
